import type { Quote, QuoteResponse } from '~/store/slices/quote/types';

export type QuotePdfRow = {
  label: string;
  value: string;
  /** Sub-itens listados abaixo da linha (ex.: coberturas incluídas no clube). */
  details?: string[];
};

export type QuotePdfCoverage = {
  productName: string;
  covers: Array<{ name: string; description: string }>;
};

export type QuotePdfModel = {
  quoteId: string;
  issuedAt: string;
  validUntil: string;
  clubName: string | null;
  sellerName: string | null;
  customerName: string;
  customerDocument: string;
  equipmentLabel: string;
  equipmentValue: string;
  planRows: QuotePdfRow[];
  operationalCost: string | null;
  monthlyTotal: string;
  membershipFee: string | null;
  resumeUrl: string;
  coverages: QuotePdfCoverage[];
};

const currency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { currency: 'BRL', minimumFractionDigits: 2, style: 'currency' }).format(value);

/** `2026-09-09T00:00:00Z` → `09/09/2026`; entradas inválidas devolvem string vazia. */
export function formatPdfDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(date);
}

export function formatPdfDocument(raw: string | null | undefined): string {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length !== 11) return raw ?? '';
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

type BuildArgs = {
  quoteResponse: QuoteResponse | undefined;
  selectedQuotes: Quote[];
  installments: number;
  customerName: string;
  customerDocument: string;
  equipmentLabel: string;
  equipmentValue: number;
  monthlyTotal: number;
  operationalCost: number;
  membershipFee: number;
  clubName: string | null;
  sellerName: string | null;
  resumeUrl: string;
};

/**
 * Monta o modelo do PDF da cotação (Figma 1700:48 / 1700:132) a partir da store.
 *
 * Mantido puro e separado do componente para ser testável sem render.
 */
export function buildQuotePdfModel({
  quoteResponse,
  selectedQuotes,
  installments,
  customerName,
  customerDocument,
  equipmentLabel,
  equipmentValue,
  monthlyTotal,
  operationalCost,
  membershipFee,
  clubName,
  sellerName,
  resumeUrl
}: BuildArgs): QuotePdfModel {
  const planRows: QuotePdfRow[] = selectedQuotes.map((quote) => ({
    label: quote.product_name,
    value: installments > 0 ? `${installments} x ${currency(quote.product_price)}` : currency(quote.product_price),
    details: quote.cover?.map((cover) => cover.cover_name).filter(Boolean)
  }));

  const coverages: QuotePdfCoverage[] = selectedQuotes
    .filter((quote) => (quote.cover?.length ?? 0) > 0)
    .map((quote) => ({
      productName: quote.product_name,
      covers: quote.cover.map((cover) => ({ description: cover.cover_description, name: cover.cover_name }))
    }));

  return {
    clubName,
    coverages,
    customerDocument: formatPdfDocument(customerDocument),
    customerName,
    equipmentLabel,
    equipmentValue: currency(equipmentValue),
    issuedAt: formatPdfDate(quoteResponse?.quote_date),
    membershipFee: membershipFee > 0 ? currency(membershipFee) : null,
    monthlyTotal: currency(monthlyTotal),
    operationalCost: operationalCost > 0 ? currency(operationalCost) : null,
    planRows,
    quoteId: quoteResponse?.quote_id ?? '',
    resumeUrl,
    sellerName,
    validUntil: formatPdfDate(quoteResponse?.quote_expiry_date)
  };
}
